import { Padding, PlottedSeries, CompositeSeries, TimeDataPoint } from '../../../interfaces/Charts';
import { useMemo } from 'react';
import { useZoom } from '../zoom';

import { scaleTime, scaleLinear, ScaleLinear, ScaleTime } from 'd3-scale';
import { line, area } from 'd3-shape';
import { extent } from 'd3-array';
import { interpolateRoundToFixed, mapSeriesToPlottedSeries } from '../helpers';
import { DEFAULT_PADDING } from '../helpers/config';
import { useNicedTicks } from './useNicedTicks';

interface CompositeHookParams {
  right: CompositeSeries;
  left: CompositeSeries;
  width: number;
  height: number;
  padding?: Padding;
}

const defaultParams = {
  padding: DEFAULT_PADDING,
};

const generateAxisPathFunction = (
  compositeSeries: CompositeSeries,
  timeScale: ScaleTime<number, number>,
  valueScale: ScaleLinear<number, number>
) => {
  switch (compositeSeries.type) {
    case 'line':
      return line<TimeDataPoint>()
        .defined(d => {
          const x = timeScale(d.timeStamp);
          const [rangeMin, rangeMax] = timeScale.range();
          return x <= rangeMax && x >= rangeMin;
        })
        .x(p => timeScale(p.timeStamp))
        .y(p => valueScale(p.value));

    case 'area':
      return area<TimeDataPoint>()
        .defined(d => {
          const x = timeScale(d.timeStamp);
          const [rangeMin, rangeMax] = timeScale.range();
          return x <= rangeMax && x >= rangeMin;
        })
        .x(p => timeScale(p.timeStamp))
        .y1(p => valueScale(p.value))
        .y0(valueScale.range()[0]);
  }
};

const useCompositeSeries = (params: CompositeHookParams) => {
  const { zoomed, zoomCoordinates } = useZoom();
  const { left, right, width, height } = params;
  const padding = params.padding ? params.padding : defaultParams.padding;

  const extremes = useMemo(() => {
    const [dMin, dMax] = extent(
      left.series
        .map(l => l.points)
        .flat()
        .concat(right.series.map(r => r.points).flat()),
      point => point.timeStamp
    );
    let lMax = 100;
    let rMax = 100;
    if (!left.isPercentage) {
      lMax = extent(left.series.map(l => l.points).flat(), p => p.value)[1] as number;
    }
    if (!right.isPercentage) {
      rMax = extent(right.series.map(l => l.points).flat(), p => p.value)[1] as number;
    }
    return { dateMin: dMin, dateMax: dMax, leftMax: lMax, rightMax: rMax };
  }, [left.isPercentage, left.series, right.isPercentage, right.series]);

  const { nicedMax: leftMax, ticks: leftTicks } = useNicedTicks(extremes.leftMax);
  const { nicedMax: rightMax, ticks: rightTicks } = useNicedTicks(extremes.rightMax);
  return useMemo(() => {
    const { dateMin, dateMax } = extremes;
    const graphBottom = height - padding.bottom;
    const graphTop = padding.top;

    const timeScale = scaleTime()
      .domain([dateMin as Date, dateMax as Date])
      .interpolate(interpolateRoundToFixed(2))
      .range([padding.left, width - padding.right]);

    const leftScale = scaleLinear()
      .domain([0, leftMax])
      .interpolate(interpolateRoundToFixed(2))
      .range([graphBottom, leftMax > 0 ? graphTop : graphBottom]);
    const rightScale = scaleLinear()
      .domain([0, rightMax])
      .interpolate(interpolateRoundToFixed(2))
      .range([graphBottom, rightMax > 0 ? graphTop : graphBottom]);

    if (zoomed && zoomCoordinates) {
      const { x1, x2 } = zoomCoordinates;
      timeScale
        .domain([x1, x2])
        .interpolate(interpolateRoundToFixed(2))
        .range([padding.left, width - padding.right]);
    }
    const leftAxisFn = generateAxisPathFunction(left, timeScale, leftScale);
    const rightAxisFn = generateAxisPathFunction(right, timeScale, rightScale);

    const leftPlottedSeries: PlottedSeries[] = left.series.map(
      mapSeriesToPlottedSeries(leftAxisFn, timeScale, leftScale)
    );
    const rightPlottedSeries: PlottedSeries[] = right.series.map(
      mapSeriesToPlottedSeries(rightAxisFn, timeScale, rightScale)
    );

    return {
      leftPlottedSeries,
      rightPlottedSeries,
      leftScale,
      rightScale,
      timeScale,
      leftTicks,
      rightTicks,
    };
  }, [
    extremes,
    height,
    padding.bottom,
    padding.top,
    padding.left,
    padding.right,
    width,
    leftMax,
    rightMax,
    zoomed,
    zoomCoordinates,
    left,
    right,
    leftTicks,
    rightTicks,
  ]);
};

export { useCompositeSeries };
